import { useRef, useState } from 'react';

/**
 * ReactBits — Magnet.
 *
 * Wraps any child element and gently pulls it toward the cursor whenever the
 * cursor enters its "magnet field" radius. Pure JS + CSS transform, no
 * external animation library.
 *
 * Props:
 *   padding         — magnet field radius beyond the element bounds (px)
 *   disabled        — turn the effect off
 *   magnetStrength  — divisor on the offset (higher = subtler pull)
 *   activeTransition / inactiveTransition — CSS transitions
 *   wrapperClassName / innerClassName — class hooks
 */
const Magnet = ({
  children,
  padding = 80,
  disabled = false,
  magnetStrength = 5,
  activeTransition = 'transform 0.25s ease-out',
  inactiveTransition = 'transform 0.45s ease-in-out',
  wrapperClassName = '',
  innerClassName = '',
  ...rest
}) => {
  const wrapRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [active, setActive] = useState(false);

  const handleMouseMove = (e) => {
    if (disabled) return;
    const el = wrapRef.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const cx = left + width / 2;
    const cy = top + height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;

    const inFieldX = Math.abs(dx) < width / 2 + padding;
    const inFieldY = Math.abs(dy) < height / 2 + padding;

    if (inFieldX && inFieldY) {
      setActive(true);
      setPosition({ x: dx / magnetStrength, y: dy / magnetStrength });
    } else {
      setActive(false);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseLeave = () => {
    setActive(false);
    setPosition({ x: 0, y: 0 });
  };

  if (disabled) {
    return (
      <div ref={wrapRef} className={wrapperClassName} {...rest}>
        <div className={innerClassName}>{children}</div>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={wrapperClassName}
      style={{ display: 'inline-block', position: 'relative' }}
      {...rest}
    >
      <div
        className={innerClassName}
        style={{
          willChange: 'transform',
          transition: active ? activeTransition : inactiveTransition,
          transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default Magnet;
